import {
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline';
import { useAnimate } from 'motion/react';
import React, { useRef } from 'react';

const ContactSupportButton = (): JSX.Element => {
  const [scope, animate] = useAnimate();
  const animationRef = useRef<Promise<void> | null>(null);

  const handleMouseEnter = async (): Promise<void> => {
    // Cancel any ongoing animation
    if (animationRef.current !== null) {
      return;
    }
    animationRef.current = (async () => {
      // Fade to EnvelopeIcon (both animations happen simultaneously)
      await Promise.all([
        animate('.icon-chat', { opacity: 0 }, { duration: 0.3 }),
        animate('.icon-envelope', { opacity: 1 }, { duration: 0.3 }),
      ]);

      // Pause 200ms then fade to EnvelopeOpenIcon
      await new Promise((resolve) => setTimeout(resolve, 200));
      await Promise.all([
        animate('.icon-envelope', { opacity: 0 }, { duration: 0.3 }),
        animate('.icon-envelope-open', { opacity: 1 }, { duration: 0.3 }),
      ]);

      // Pause 500ms then fade to FaceSmileIcon
      await new Promise((resolve) => setTimeout(resolve, 500));
      await Promise.all([
        animate('.icon-envelope-open', { opacity: 0 }, { duration: 0.3 }),
        animate('.icon-smile', { opacity: 1 }, { duration: 0.3 }),
      ]);

      // Pause 500ms then fade to ChatBubbleIcon (loop back to start)
      await new Promise((resolve) => setTimeout(resolve, 500));
      await Promise.all([
        animate('.icon-smile', { opacity: 0 }, { duration: 0.3 }),
        animate('.icon-chat', { opacity: 1 }, { duration: 0.3 }),
      ]);
    })();

    await animationRef.current;
    animationRef.current = null;
  };

  const handleMouseLeave = async (): Promise<void> => {
    // Reset to initial state
    animationRef.current = null;

    // Fade all back to initial state (all simultaneously)
    await Promise.all([
      animate('.icon-chat', { opacity: 1 }, { duration: 0.3 }),
      animate('.icon-envelope', { opacity: 0 }, { duration: 0.3 }),
      animate('.icon-envelope-open', { opacity: 0 }, { duration: 0.3 }),
      animate('.icon-smile', { opacity: 0 }, { duration: 0.3 }),
    ]);
  };

  const getBrowserInfo = (): string => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let version = '';

    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      browser = 'Chrome';
      const match = ua.match(/Chrome\/(\d+\.\d+)/);
      version = match !== null ? match[1] : '';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browser = 'Safari';
      const match = ua.match(/Version\/(\d+\.\d+)/);
      version = match !== null ? match[1] : '';
    } else if (ua.includes('Firefox')) {
      browser = 'Firefox';
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      version = match !== null ? match[1] : '';
    } else if (ua.includes('Edg')) {
      browser = 'Edge';
      const match = ua.match(/Edg\/(\d+\.\d+)/);
      version = match !== null ? match[1] : '';
    }

    return version !== '' ? `${browser} ${version}` : browser;
  };

  const getOSInfo = (): string => {
    const ua = navigator.userAgent;
    if (ua.includes('Mac OS X')) {
      return 'macOS';
    } else if (ua.includes('Windows')) {
      return 'Windows';
    } else if (ua.includes('Linux')) {
      return 'Linux';
    } else if (ua.includes('iPhone') || ua.includes('iPad')) {
      return 'iOS';
    } else if (ua.includes('Android')) {
      return 'Android';
    }
    return 'Unknown';
  };

  const generateMailtoLink = (): string => {
    const supportEmail = 'support@dataops.live';
    // If path is `/` change to `dashboard` for clarity
    const path = window.location.pathname === '/' ? '/dashboard' : window.location.pathname;
    const subject = encodeURIComponent(`Support for DataOps.live Admin (${path}${window.location.search})`);

    const timestamp = new Date().toISOString();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browser = getBrowserInfo();
    const os = getOSInfo();
    const screenInfo = `${window.screen.width}x${window.screen.height} (viewport: ${window.innerWidth}x${window.innerHeight})`;

    // Body structure:
    // "Hi DataOps.live Support,"
    // "I need assistance with..."
    // "Support Information: <detailed info>"
    const body = encodeURIComponent(`Hi DataOps.live Support,

I need assistance with...

Support Information:
- URL: ${window.location.href}
- Browser: ${browser}
- Screen: ${screenInfo}
- OS: ${os}
- Timestamp: ${timestamp}
- Time Zone: ${timeZone}
- User Agent: ${navigator.userAgent}`);
    return `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };
  return (
    <a
      href={generateMailtoLink()}
      onMouseEnter={() => {
        void handleMouseEnter();
      }}
      onMouseLeave={() => {
        void handleMouseLeave();
      }}
      className="fixed bottom-0 right-1/2 transform translate-x-1/2 sm:translate-x-0 sm:bottom-6 sm:right-6 z-50 bg-dataops-dark hover:bg-dataops-secondary-blue text-white px-6 py-3 rounded-full shadow-lg transition-colors duration-200 flex items-center gap-2"
      aria-label="Contact Support"
    >
      <div ref={scope} className="relative w-5 h-5">
        <ChatBubbleOvalLeftEllipsisIcon className="icon-chat absolute inset-0 w-5 h-5 stroke-2" />
        <EnvelopeIcon className="icon-envelope absolute inset-0 w-5 h-5 stroke-2 opacity-0" />
        <EnvelopeOpenIcon className="icon-envelope-open absolute inset-0 w-5 h-5 stroke-2 opacity-0" />
        <FaceSmileIcon className="icon-smile absolute inset-0 w-5 h-5 stroke-2 opacity-0" />
      </div>
      <span>Contact Support</span>
    </a>
  );
};

export default ContactSupportButton;
